// @ts-check

import { html } from "lit-html";
import {
  PRIMARY_BUTTON_CLASS,
  SECONDARY_BUTTON_CLASS,
  renderSectionCard,
  renderSteppedNumberField,
} from "../ui.js";
import { copyTextToClipboard, hasText, parseNumber } from "../utils.js";

/** @typedef {ReturnType<typeof html>} LitView */
/** @typedef {import("./index.js").ToolEnv} ToolEnv */
/** @typedef {ReturnType<typeof createNanoidGeneratorState>} NanoidGeneratorState */

const ID_ALPHABET = "23456789ABCDEFGHJKMNPQRSTVWXYZ";
const DEFAULT_ID_SIZE = "8";
const MAX_ID_SIZE = 512;
const ALPHABET_FIELD_CLASS =
  "h-11 w-full rounded-lg border border-stone-300 bg-white px-3 font-mono text-base text-stone-900 outline-none transition focus:border-stone-500 focus:ring-2 focus:ring-stone-400/20 sm:px-4";
const ICON_BUTTON_CONTENT_CLASS =
  "inline-flex min-w-0 items-center justify-center gap-1.5";

export const nanoidGeneratorTool = {
  key: "nanoid-generator",
  path: "/nanoid-generator",
  title: "Nanoid Generator",
  blurb: "Generate uppercase IDs without confusing characters.",
  badge: "Random",
  accentClass: "bg-gradient-to-r from-emerald-300 via-cyan-300 to-sky-300",
  createState: createNanoidGeneratorState,
  render: renderNanoidGeneratorPage,
};

function createNanoidGeneratorState() {
  return {
    size: DEFAULT_ID_SIZE,
    alphabet: ID_ALPHABET,
    isAlphabetEditable: false,
    result: generateNanoid(Number(DEFAULT_ID_SIZE), ID_ALPHABET),
    copyStatus: "idle",
  };
}

/**
 * @param {NanoidGeneratorState} toolState
 * @param {ToolEnv} env
 * @returns {LitView}
 */
function renderNanoidGeneratorPage(toolState, env) {
  const sizeValidation = validateIdSize(toolState.size);
  const alphabetValidation = validateAlphabet(toolState.alphabet);
  const isGenerateDisabled =
    sizeValidation.kind === "error" || alphabetValidation.kind === "error";
  const canCopy = hasText(toolState.result);
  const copyButtonClass = getCopyButtonClass(toolState.copyStatus);
  const copyButtonLabel = getCopyButtonLabel(toolState.copyStatus);

  return html`
    <section class="grid min-h-[calc(100dvh-8rem)] content-start gap-4">
      ${renderSectionCard(
        "Generator",
        "",
        html`
          <div class="grid gap-4">
            <div
              class="min-w-0 rounded-lg bg-stone-100 px-4 py-5 text-center sm:px-6 sm:py-7"
            >
              <div class="text-xs font-semibold uppercase text-stone-400">
                Nanoid
              </div>
              <output
                class="mt-2 block break-all font-mono text-4xl font-semibold text-stone-950 sm:text-6xl"
                >${toolState.result}</output>
            </div>

            <div class="grid gap-4 sm:grid-cols-[14rem_1fr] sm:items-end">
              ${renderSteppedNumberField({
                label: "ID size",
                value: toolState.size,
                placeholder: DEFAULT_ID_SIZE,
                help: "Default is 8 characters.",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "size", event);
                },
                onStepDown: function () {
                  stepIdSize(toolState, env, -1);
                },
                onStepUp: function () {
                  stepIdSize(toolState, env, 1);
                },
              })}
              ${renderAlphabetField(toolState, env)}
            </div>

            <div class="flex flex-wrap gap-3">
              <button
                class=${PRIMARY_BUTTON_CLASS}
                type="button"
                ?disabled=${isGenerateDisabled}
                @click=${function () {
                  generateIdResult(toolState, env);
                }}
              >
                Generate
              </button>
              <button
                class=${copyButtonClass}
                type="button"
                ?disabled=${!canCopy}
                @click=${async function () {
                  await copyResult(toolState, env);
                }}
              >
                <span class=${ICON_BUTTON_CONTENT_CLASS}>
                  ${renderCopyIcon()}
                  <span>${copyButtonLabel}</span>
                </span>
              </button>
              <button
                class=${SECONDARY_BUTTON_CLASS}
                type="button"
                @click=${function () {
                  toolState.size = DEFAULT_ID_SIZE;
                  toolState.alphabet = ID_ALPHABET;
                  toolState.isAlphabetEditable = false;
                  toolState.result = generateNanoid(
                    Number(DEFAULT_ID_SIZE),
                    ID_ALPHABET,
                  );
                  toolState.copyStatus = "idle";
                  env.scheduleRender();
                }}
              >
                Reset
              </button>
            </div>

            ${sizeValidation.kind === "error"
              ? html`<p class="text-sm font-medium text-red-600">
                  ${sizeValidation.error}
                </p>`
              : ""}
            ${alphabetValidation.kind === "error"
              ? html`<p class="text-sm font-medium text-red-600">
                  ${alphabetValidation.error}
                </p>`
              : ""}
          </div>
        `,
      )}
    </section>
  `;
}

/**
 * @param {NanoidGeneratorState} toolState
 * @param {ToolEnv} env
 * @returns {LitView}
 */
function renderAlphabetField(toolState, env) {
  const editButtonLabel = toolState.isAlphabetEditable ? "Done" : "Edit";

  return html`
    <div class="grid min-w-0 gap-1.5 text-sm font-medium text-stone-700">
      <span class="flex min-w-0 items-baseline justify-between gap-3">
        <label for="nanoid-alphabet">Alphabet</label>
        <button
          class="shrink-0 text-xs font-semibold text-stone-500 underline underline-offset-2 transition hover:text-stone-900"
          type="button"
          @click=${function () {
            toggleAlphabetEditing(toolState, env);
          }}
        >
          ${editButtonLabel}
        </button>
      </span>
      <input
        id="nanoid-alphabet"
        class=${ALPHABET_FIELD_CLASS}
        type="text"
        .value=${toolState.alphabet}
        placeholder=${ID_ALPHABET}
        ?readonly=${!toolState.isAlphabetEditable}
        @input=${
          /**
           * @param {Event} event
           * @returns {void}
           */
          function (event) {
            handleAlphabetInput(toolState, env, event);
          }
        }
      />
      <span class="text-xs font-normal leading-5 text-stone-400">
        Uppercase characters are used. Repeated characters are ignored.
      </span>
    </div>
  `;
}

/**
 * @param {NanoidGeneratorState} toolState
 * @param {ToolEnv} env
 * @returns {void}
 */
function toggleAlphabetEditing(toolState, env) {
  if (toolState.isAlphabetEditable) {
    const alphabetValidation = validateAlphabet(toolState.alphabet);
    if (alphabetValidation.kind === "ready") {
      toolState.alphabet = alphabetValidation.alphabet;
    }
  }

  toolState.isAlphabetEditable = !toolState.isAlphabetEditable;
  env.scheduleRender();
}

/**
 * @param {NanoidGeneratorState} toolState
 * @param {ToolEnv} env
 * @returns {void}
 */
function generateIdResult(toolState, env) {
  const sizeValidation = validateIdSize(toolState.size);
  if (sizeValidation.kind === "error") {
    return;
  }

  const alphabetValidation = validateAlphabet(toolState.alphabet);
  if (alphabetValidation.kind === "error") {
    return;
  }

  toolState.alphabet = alphabetValidation.alphabet;
  toolState.result = generateNanoid(
    sizeValidation.size,
    alphabetValidation.alphabet,
  );
  toolState.copyStatus = "idle";
  env.scheduleRender();
}

/**
 * @param {NanoidGeneratorState} toolState
 * @param {ToolEnv} env
 * @param {Event} event
 * @returns {void}
 */
function handleAlphabetInput(toolState, env, event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }

  toolState.alphabet = target.value.toUpperCase();
  env.scheduleRender();
}

/**
 * @param {NanoidGeneratorState} toolState
 * @param {ToolEnv} env
 * @returns {Promise<void>}
 */
async function copyResult(toolState, env) {
  const clipboardText = toolState.result.trim();
  if (!hasText(clipboardText)) {
    return;
  }

  try {
    await copyTextToClipboard(clipboardText);
    env.setTemporaryStatus(
      toolState,
      "copyStatus",
      "copied",
      "idle",
      "nanoidGenerator",
    );
  } catch (error) {
    console.error("[app] copy nanoid failed", error);
    env.setTemporaryStatus(
      toolState,
      "copyStatus",
      "error",
      "idle",
      "nanoidGenerator",
    );
  }
}

/**
 * @param {NanoidGeneratorState} toolState
 * @param {ToolEnv} env
 * @param {number} direction
 * @returns {void}
 */
function stepIdSize(toolState, env, direction) {
  const currentSize = getIdSize(toolState.size);
  const nextSize = Math.min(MAX_ID_SIZE, Math.max(1, currentSize + direction));
  toolState.size = String(nextSize);
  toolState.copyStatus = "idle";
  env.scheduleRender();
}

/**
 * @param {string} sizeText
 * @returns {{ kind: "ready", size: number } | { kind: "error", error: string }}
 */
function validateIdSize(sizeText) {
  const size = getIdSize(sizeText);
  let result =
    /** @type {{ kind: "ready", size: number } | { kind: "error", error: string }} */ ({
      kind: "ready",
      size,
    });

  if (!hasText(sizeText) || size < 1 || size > MAX_ID_SIZE) {
    result = {
      kind: "error",
      error: `Enter an ID size from 1 to ${MAX_ID_SIZE}.`,
    };
  }

  return result;
}

/**
 * @param {string} sizeText
 * @returns {number}
 */
function getIdSize(sizeText) {
  let size = Number(DEFAULT_ID_SIZE);
  const parsedSize = parseNumber(sizeText);
  if (Number.isFinite(parsedSize)) {
    size = Math.trunc(parsedSize);
  }

  return size;
}

/**
 * @param {string} alphabetText
 * @returns {{ kind: "ready", alphabet: string } | { kind: "error", error: string }}
 */
function validateAlphabet(alphabetText) {
  const alphabet = normalizeAlphabet(alphabetText);
  let result =
    /** @type {{ kind: "ready", alphabet: string } | { kind: "error", error: string }} */ ({
      kind: "ready",
      alphabet,
    });

  if (alphabet.length < 2 || alphabet.length > 255) {
    result = {
      kind: "error",
      error: "Enter 2 to 255 unique alphabet characters.",
    };
  }

  return result;
}

/**
 * @param {string} alphabetText
 * @returns {string}
 */
function normalizeAlphabet(alphabetText) {
  const uppercaseAlphabet = alphabetText.toUpperCase().replace(/\s+/g, "");
  let alphabet = "";
  for (
    let index = 0, total = uppercaseAlphabet.length;
    index < total;
    index += 1
  ) {
    const character = uppercaseAlphabet[index];
    if (alphabet.includes(character)) {
      continue;
    }

    alphabet += character;
  }

  return alphabet;
}

/**
 * @param {number} size
 * @param {string} alphabet
 * @returns {string}
 */
function generateNanoid(size, alphabet) {
  const alphabetLength = alphabet.length;
  const byteLimit = Math.floor(256 / alphabetLength) * alphabetLength;
  let id = "";

  while (id.length < size) {
    const remainingSize = size - id.length;
    const randomBytes = crypto.getRandomValues(new Uint8Array(remainingSize));
    for (let index = 0, total = randomBytes.length; index < total; index += 1) {
      const randomByte = randomBytes[index];
      if (randomByte >= byteLimit) {
        continue;
      }

      const alphabetIndex = randomByte % alphabetLength;
      id += alphabet[alphabetIndex];
      if (id.length === size) {
        break;
      }
    }
  }

  return id;
}

/**
 * @returns {LitView}
 */
function renderCopyIcon() {
  return html`
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      class="size-6 shrink-0"
      aria-hidden="true"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
      />
    </svg>
  `;
}

/**
 * @param {string} copyStatus
 * @returns {string}
 */
function getCopyButtonClass(copyStatus) {
  let result = SECONDARY_BUTTON_CLASS;
  if (copyStatus === "copied") {
    result =
      "inline-flex h-11 items-center justify-center rounded-lg bg-emerald-700 px-4 text-sm font-semibold text-white";
  }

  if (copyStatus === "error") {
    result =
      "inline-flex h-11 items-center justify-center rounded-lg bg-red-600 px-4 text-sm font-semibold text-white";
  }

  return result;
}

/**
 * @param {string} copyStatus
 * @returns {string}
 */
function getCopyButtonLabel(copyStatus) {
  let result = "Copy";
  if (copyStatus === "copied") {
    result = "Copied";
  }

  if (copyStatus === "error") {
    result = "Copy failed";
  }

  return result;
}
